/**
 * The HUD model (plan G10, spec §10): everything the combat HUD shows, computed per frame from the sim's
 * components, the combat event ring and the live camera into one preallocated `HudState`. Pure of the DOM
 * (the view, hud.ts, turns it into element writes). Positions are interpolated with the frame's `alpha` like
 * the ship views, so markers sit exactly on the rendered ships. Float64 world maths throughout (D-011);
 * allocation-free per frame (D-014).
 */
import { z } from 'zod'
import type { Vec3 } from '../core/math'
import { eventSlot, EV, firstReadable, type EventRing } from '../combat/events'
import { assistCorrection } from '../combat/aimAssist'
import { createLeadSolution, solveLead } from '../combat/lead'
import { isHostile, isPlayerSide, PLAYER_TEAM_ID, TEAM_SLOTS } from '../combat/hostility'
import { lockProgress } from '../combat/lock'
import type { ShipDef, WeaponsData } from '../data/schemas'
import factionsJson from '../data/strategic/factions.json'
import type { HudTuning } from '../features/hud/tuning'
import type { CameraMode } from '../render/cameraRig'
import { kitNameOf } from '../ships/kitSlots'
import { Energy, Hull, Life, Lock, Pos64, PrevPos64, PrevRot, Rot, Shield, Ship, Target, Team, Vel, Weapons } from '../world/components'
import { createScreenPoint, directionAngle, projectDirection, projectWorld, type HudCamera, type ScreenPoint } from './projection'
import readoutsJson from './hud-readouts.json'
import { formatSpeed, speedKey } from './reach/format'

const readoutsSchema = z.object({
  cameraToastSeconds: z.number().positive(),
  cameraToastFadeSeconds: z.number().nonnegative(),
  text: z.object({
    faOn: z.string(),
    faOff: z.string(),
    cameraToast: z.object({ chase: z.string(), cockpit: z.string() }),
    targetPrefix: z.string(),
    closing: z.string(),
    opening: z.string(),
    separator: z.string(),
    stanceGlyph: z.object({ hostile: z.string(), neutral: z.string(), friendly: z.string() }),
    ownTeam: z.string(),
    unknownFaction: z.string(),
  }),
})

/** HUD readout text and timings (hud-readouts.json; dashboard spec §6 H1, H4, H5). */
export const HUD_READOUTS = readoutsSchema.parse(readoutsJson)

/** Target stance toward the player (spec §5.1: hostile, neutral, friendly each get a shape glyph). */
export const STANCE = { hostile: 0, neutral: 1, friendly: 2 } as const
export type Stance = (typeof STANCE)[keyof typeof STANCE]
/** Glyph by `Stance` (◆ ▲ ●). */
export const STANCE_GLYPHS: readonly string[] = [
  HUD_READOUTS.text.stanceGlyph.hostile,
  HUD_READOUTS.text.stanceGlyph.neutral,
  HUD_READOUTS.text.stanceGlyph.friendly,
]

/** Faction display name by M1 team id (factions.json `teamId`; the player's team is "Your fleet"). Built once. */
const TEAM_NAMES: readonly string[] = (() => {
  const names: string[] = []
  for (let i = 0; i < TEAM_SLOTS; i++) names.push(HUD_READOUTS.text.unknownFaction)
  names[PLAYER_TEAM_ID] = HUD_READOUTS.text.ownTeam
  const factions = z.object({ factions: z.array(z.object({ name: z.string(), teamId: z.number().int() })) }).parse(factionsJson).factions
  for (const f of factions) if (f.teamId >= 0 && f.teamId < TEAM_SLOTS) names[f.teamId] = f.name
  return names
})()

/** The HUD speed key: the same as the cruise panel's (`reach/format` `speedKey`), so both read one value. */
export const hudSpeedKey = speedKey

/**
 * The flight speed readout split into numeral and unit, from the one shared formatter (`formatSpeed`: m/s, km/s,
 * Mm/s, at most 4 significant digits). Called only when `hudSpeedKey` changes.
 */
export function formatHudSpeed(mps: number): { value: string; unit: string } {
  const text = formatSpeed(mps)
  const space = text.lastIndexOf(' ')
  return { value: text.slice(0, space), unit: text.slice(space + 1) }
}

interface TargetLabels {
  name: string
  cls: string
}

/** Name and class per hull, split once from `ShipDef.name` ("Varro SI-7 Jackal (raider interceptor)"). */
const labelCache = new WeakMap<ShipDef, TargetLabels>()

/** The target labels of `def`; built the first time a hull is targeted, then cached (no per-frame allocation). */
function labelsOf(def: ShipDef): TargetLabels {
  const hit = labelCache.get(def)
  if (hit) return hit
  const open = def.name.indexOf(' (')
  const close = def.name.lastIndexOf(')')
  const name = open > 0 ? def.name.slice(0, open) : def.name
  const raw = open > 0 && close > open ? def.name.slice(open + 2, close) : ''
  const labels = { name, cls: raw.length > 0 ? raw[0].toUpperCase() + raw.slice(1) : '' }
  labelCache.set(def, labels)
  return labels
}

/** Team id of the raiders (sim.ts `RAIDER_TEAM`; kept local so the model does not pull in Rapier). */
export const HOSTILE_TEAM = 1

/** Hit-marker kinds, in rising priority. */
export const HIT = { none: 0, shield: 1, hull: 2, kill: 3 } as const
export type HitKind = (typeof HIT)[keyof typeof HIT]

/** Velocity-vector marker modes. */
export const VV = { hidden: 0, prograde: 1, retrograde: 2 } as const
export type VelocityMode = (typeof VV)[keyof typeof VV]

/** Damage-direction wedges in the pool. */
export const WEDGE_COUNT = 8

/** What the model reads each frame. The feature refills one instance in place. */
export interface HudSource {
  playerEid: number
  /** Live ship entity ids (`sim.ships.dense`). */
  ships: ArrayLike<number>
  defs: readonly ShipDef[]
  weapons: WeaponsData
  events: EventRing
  /** Sim time (s). */
  time: number
  /** Interpolation factor between the previous and the current tick. */
  alpha: number
  /** Real seconds since the last frame (HUD timers advance by it unless paused). */
  dt: number
  paused: boolean
  cam: HudCamera
  cameraMode: CameraMode
  /** Mouse virtual-joystick offset (unit circle). */
  stickX: number
  stickY: number
}

export interface HudTargetState {
  /** Selected target entity, -1 = none. */
  eid: number
  /** Another team than the player's (brackets differ by shape and colour, style guide §9.5). */
  hostile: boolean
  /** Metres from the player. */
  distance: number
  hull: number
  shield: number
  /** The target's screen position, or the edge-arrow position and angle when off screen. */
  point: ScreenPoint
  /** Bracket half-size (CSS px). */
  halfPx: number
  /** Lead pip for the selected primary's projectile speed (`solveLead`). */
  leadVisible: boolean
  lead: ScreenPoint
  /** The lead point lies inside the gimbal cone of the nose: shots will be aimed at it (spec §6). */
  leadInGimbal: boolean
  /** Aim assist on the lead point (D-247): 0 none, 1 easing the guns toward it, 2 the guns converge on it. */
  assist: number
  /** Target card (H4): hull name, class and faction (cached strings, compared by identity), and the stance. */
  name: string
  cls: string
  faction: string
  stance: Stance
  /** Closing speed (m/s): positive while the range shrinks, negative while it opens. */
  closing: number
  /**
   * RR-F1 BL-2b: the purpose label of a promoted traffic ship ("Ferrite → Halvard Refinery"; a cached string from a
   * contact source's `labelOf`), '' = none. When set, `cls` shows it on the line under the hull name.
   */
  purpose: string
}

export interface HudWedge {
  active: boolean
  /** World-space unit direction from the player toward the hit. */
  dx: number
  dy: number
  dz: number
  /** Seconds since the (last merged) hit. */
  age: number
  kind: HitKind
  /** Screen angle around the centre for the current camera (0 = up, clockwise, radians). */
  angle: number
  /** 1 at the hit, fading to 0 over `wedge.fadeSeconds`. */
  intensity: number
}

export interface HudState {
  /** The player exists and is alive. */
  alive: boolean
  /** Seconds until respawn while dead. */
  respawnIn: number
  speed: number
  faOn: boolean
  boosting: boolean
  hull: number
  shield: number
  energy: number
  lowHull: boolean
  /** Selected primary: short label (a tuning string, never built per frame) and slot (0/1). */
  weaponLabel: string
  weaponSlot: number
  hasTorpedo: boolean
  torpedoes: number
  /** Gun cross: where the nose points, at the target's distance (or `aimDistance`). */
  boresight: ScreenPoint
  /** Aim assist circle radius around the gun cross (CSS px); 0 when the assist is off. */
  assistRadiusPx: number
  velocityMode: VelocityMode
  velocity: ScreenPoint
  target: HudTargetState
  /** Torpedo lock progress 0..1 and the locked flag (`Lock`). */
  lockProgress: number
  locked: boolean
  /** Seconds since our last hit, and its kind (the highest kind within one frame wins). */
  hitAge: number
  hitKind: HitKind
  wedges: HudWedge[]
  /** Every other visible living ship (not the player, not the target): screen x/y and hostility. */
  contactX: Float32Array
  contactY: Float32Array
  contactHostile: Uint8Array
  contactCount: number
  /** Last `waveStart` event's wave number (0 before the first wave) and seconds since it. */
  wave: number
  waveAge: number
  /** Living hostile ships. */
  enemies: number
  cameraMode: CameraMode
  /** Real seconds since the last camera switch (H5 toast); Infinity before the first switch. */
  cameraToastAge: number
  /** The camera mode has been read once (the first frame never toasts). */
  cameraKnown: boolean
  stickX: number
  stickY: number
  /** Event-ring cursor. */
  cursor: number
}

function createTargetState(): HudTargetState {
  return {
    eid: -1,
    hostile: true,
    distance: 0,
    hull: 0,
    shield: 0,
    point: createScreenPoint(),
    halfPx: 0,
    leadVisible: false,
    lead: createScreenPoint(),
    leadInGimbal: false,
    assist: 0,
    name: '',
    cls: '',
    purpose: '',
    faction: '',
    stance: STANCE.hostile,
    closing: 0,
  }
}

export function createHudState(maxContacts: number): HudState {
  const wedges: HudWedge[] = []
  for (let i = 0; i < WEDGE_COUNT; i++) wedges.push({ active: false, dx: 0, dy: 0, dz: -1, age: 0, kind: HIT.shield, angle: 0, intensity: 0 })
  return {
    alive: false,
    respawnIn: 0,
    speed: 0,
    faOn: true,
    boosting: false,
    hull: 1,
    shield: 1,
    energy: 1,
    lowHull: false,
    weaponLabel: '',
    weaponSlot: 0,
    hasTorpedo: false,
    torpedoes: 0,
    boresight: createScreenPoint(),
    assistRadiusPx: 0,
    velocityMode: VV.hidden,
    velocity: createScreenPoint(),
    target: createTargetState(),
    lockProgress: 0,
    locked: false,
    hitAge: Infinity,
    hitKind: HIT.none,
    wedges,
    contactX: new Float32Array(maxContacts),
    contactY: new Float32Array(maxContacts),
    contactHostile: new Uint8Array(maxContacts),
    contactCount: 0,
    wave: 0,
    waveAge: Infinity,
    enemies: 0,
    cameraMode: 'chase',
    cameraToastAge: Infinity,
    cameraKnown: false,
    stickX: 0,
    stickY: 0,
    cursor: 0,
  }
}

// Scratch (D-014).
const playerPos: Vec3 = { x: 0, y: 0, z: 0 }
const targetPos: Vec3 = { x: 0, y: 0, z: 0 }
const otherPos: Vec3 = { x: 0, y: 0, z: 0 }
const contactPoint = createScreenPoint()
const lead = createLeadSolution()
let noseX = 0
let noseY = 0
let noseZ = -1

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v
}

/** Interpolated float64 position of `eid` (PrevPos64 → Pos64 by alpha), like the ship views. */
function interpolate(eid: number, alpha: number, out: Vec3): void {
  out.x = PrevPos64.x[eid] + (Pos64.x[eid] - PrevPos64.x[eid]) * alpha
  out.y = PrevPos64.y[eid] + (Pos64.y[eid] - PrevPos64.y[eid]) * alpha
  out.z = PrevPos64.z[eid] + (Pos64.z[eid] - PrevPos64.z[eid]) * alpha
}

/** The interpolated nose (local −Z) of `eid` in world space (normalised lerp of PrevRot → Rot), into the scratch. */
function loadNose(eid: number, alpha: number): void {
  let bx = Rot.x[eid]
  let by = Rot.y[eid]
  let bz = Rot.z[eid]
  let bw = Rot.w[eid]
  const ax = PrevRot.x[eid]
  const ay = PrevRot.y[eid]
  const az = PrevRot.z[eid]
  const aw = PrevRot.w[eid]
  if (ax * bx + ay * by + az * bz + aw * bw < 0) {
    bx = -bx
    by = -by
    bz = -bz
    bw = -bw
  }
  let qx = ax + (bx - ax) * alpha
  let qy = ay + (by - ay) * alpha
  let qz = az + (bz - az) * alpha
  let qw = aw + (bw - aw) * alpha
  const len = Math.sqrt(qx * qx + qy * qy + qz * qz + qw * qw)
  if (len > 0) {
    qx /= len
    qy /= len
    qz /= len
    qw /= len
  } else {
    qw = 1
  }
  noseX = -2 * (qx * qz + qw * qy)
  noseY = -2 * (qy * qz - qw * qx)
  noseZ = -(1 - 2 * (qx * qx + qy * qy))
}

function contains(ships: ArrayLike<number>, eid: number): boolean {
  for (let i = 0; i < ships.length; i++) if (ships[i] === eid) return true
  return false
}

/** Weapon index of the player's selected primary (the first primary mount in the selected slot), or -1. */
function selectedPrimary(def: ShipDef, slot: number): number {
  const mounts = def.mounts
  for (let i = 0; i < mounts.length; i++) {
    const m = mounts[i]
    if (m.group === 'primary' && m.slot === slot) return m.weapon
  }
  return -1
}

function hasSecondary(def: ShipDef): boolean {
  const mounts = def.mounts
  for (let i = 0; i < mounts.length; i++) if (mounts[i].group === 'secondary') return true
  return false
}

/** Records a hit on the player coming from world direction (dx, dy, dz) (not necessarily unit). */
function addWedge(state: HudState, dx: number, dy: number, dz: number, kind: HitKind, mergeCos: number): void {
  const len = Math.sqrt(dx * dx + dy * dy + dz * dz)
  if (len < 1e-9) return
  const ux = dx / len
  const uy = dy / len
  const uz = dz / len
  const wedges = state.wedges
  let slot = -1
  let free = -1
  let oldest = -1
  for (let i = 0; i < wedges.length; i++) {
    const w = wedges[i]
    if (!w.active) {
      if (free < 0) free = i
    } else if (w.dx * ux + w.dy * uy + w.dz * uz >= mergeCos) {
      slot = i
      break
    } else if (oldest < 0 || w.age > wedges[oldest].age) {
      oldest = i
    }
  }
  if (slot >= 0) {
    const w = wedges[slot]
    w.age = 0
    if (kind > w.kind) w.kind = kind
    w.dx = ux
    w.dy = uy
    w.dz = uz
    return
  }
  slot = free >= 0 ? free : oldest
  const w = wedges[slot]
  w.active = true
  w.age = 0
  w.kind = kind
  w.dx = ux
  w.dy = uy
  w.dz = uz
}

/** Reads new combat events: wave number, our hit markers and damage-direction wedges. */
function readEvents(state: HudState, src: HudSource, tuning: HudTuning): void {
  const ring = src.events
  const p = src.playerEid
  const mergeCos = Math.cos((tuning.wedge.mergeDeg * Math.PI) / 180)
  for (let i = firstReadable(ring, state.cursor); i < ring.head; i++) {
    const s = eventSlot(i)
    const type = ring.type[s]
    if (type === EV.waveStart) {
      state.wave = Math.round(ring.amount[s])
      state.waveAge = 0
    } else if (p < 0) {
      continue
    } else if (type === EV.hitShield || type === EV.hitHull || type === EV.kill) {
      const victim = ring.eid[s]
      const kind: HitKind = type === EV.kill ? HIT.kill : type === EV.hitHull ? HIT.hull : HIT.shield
      if (ring.other[s] === p && victim !== p) {
        // Our hit: the highest kind since the last frame wins.
        if (state.hitAge > 0 || kind > state.hitKind) state.hitKind = kind
        state.hitAge = 0
      } else if (victim === p && type !== EV.kill) {
        // Hit on us: the side of the hull that took it (hit point − ship centre); fall back to the attacker.
        let dx = ring.x[s] - Pos64.x[p]
        let dy = ring.y[s] - Pos64.y[p]
        let dz = ring.z[s] - Pos64.z[p]
        const a = ring.other[s]
        if (dx * dx + dy * dy + dz * dz < 1e-6 && a >= 0) {
          dx = Pos64.x[a] - Pos64.x[p]
          dy = Pos64.y[a] - Pos64.y[p]
          dz = Pos64.z[a] - Pos64.z[p]
        }
        addWedge(state, dx, dy, dz, kind, mergeCos)
      }
    } else if (type === EV.respawn && ring.eid[s] === p) {
      for (let w = 0; w < state.wedges.length; w++) state.wedges[w].active = false
    }
  }
  state.cursor = ring.head
}

function advanceTimers(state: HudState, dt: number, tuning: HudTuning): void {
  state.hitAge += dt
  state.waveAge += dt
  state.cameraToastAge += dt
  const fade = tuning.wedge.fadeSeconds
  const wedges = state.wedges
  for (let i = 0; i < wedges.length; i++) {
    const w = wedges[i]
    if (!w.active) continue
    w.age += dt
    if (w.age >= fade) {
      w.active = false
      w.intensity = 0
    }
  }
}

/** Selected target: distance, bars, screen position or edge arrow, bracket size and the lead pip. */
function updateTarget(state: HudState, src: HudSource, tuning: HudTuning, weaponIndex: number): void {
  const t = state.target
  const p = src.playerEid
  const e = Target.eid[p]
  if (e < 0 || e === p || Life.alive[e] !== 1 || !contains(src.ships, e)) {
    t.eid = -1
    t.leadVisible = false
    t.leadInGimbal = false
    t.assist = 0
    return
  }
  const def = src.defs[Ship.defIndex[e]]
  t.eid = e
  t.hostile = isHostile(Team.id[p], Team.id[e])
  interpolate(e, src.alpha, targetPos)
  const rx = targetPos.x - playerPos.x
  const ry = targetPos.y - playerPos.y
  const rz = targetPos.z - playerPos.z
  const dist = Math.sqrt(rx * rx + ry * ry + rz * rz)
  t.distance = dist
  t.closing = dist > 0 ? -((Vel.x[e] - Vel.x[p]) * rx + (Vel.y[e] - Vel.y[p]) * ry + (Vel.z[e] - Vel.z[p]) * rz) / dist : 0
  const labels = labelsOf(def)
  // M10 P4b4b: a kitted ship shows its kit's name pattern ("Second Chances, a Lifeline MF-9 Severn").
  t.name = kitNameOf(e, Ship.defIndex[e]) ?? labels.name
  t.purpose = purposeOf(e)
  t.cls = t.purpose.length > 0 ? t.purpose : labels.cls
  const team = Team.id[e]
  t.faction = TEAM_NAMES[team] ?? HUD_READOUTS.text.unknownFaction
  t.stance = t.hostile ? STANCE.hostile : isPlayerSide(team) ? STANCE.friendly : STANCE.neutral
  t.hull = clamp01(Hull.value[e] / def.hull)
  t.shield = def.shield > 0 ? clamp01(Shield.value[e] / def.shield) : 0
  projectWorld(src.cam, targetPos.x, targetPos.y, targetPos.z, tuning.edgeMarginPx, t.point)
  const b = tuning.bracket
  const half = def.colliderRadius * b.radiusScale * t.point.pxPerMeter + b.padPx
  t.halfPx = half < b.minHalfPx ? b.minHalfPx : half > b.maxHalfPx ? b.maxHalfPx : half

  // Lead pip for the selected primary (projectile weapons only).
  const w = weaponIndex >= 0 ? src.weapons.weapons[weaponIndex] : undefined
  if (w === undefined || w.kind !== 'projectile' || t.distance > w.range * tuning.leadRangeFactor) {
    t.leadVisible = false
    t.leadInGimbal = false
    t.assist = 0
    return
  }
  solveLead(playerPos.x, playerPos.y, playerPos.z, Vel.x[p], Vel.y[p], Vel.z[p], targetPos.x, targetPos.y, targetPos.z, Vel.x[e], Vel.y[e], Vel.z[e], w.speed, lead)
  t.leadVisible = projectWorld(src.cam, lead.x, lead.y, lead.z, tuning.edgeMarginPx, t.lead)
  const lx = lead.x - playerPos.x
  const ly = lead.y - playerPos.y
  const lz = lead.z - playerPos.z
  const ll = Math.sqrt(lx * lx + ly * ly + lz * lz)
  const cos = ll > 0 ? (lx * noseX + ly * noseY + lz * noseZ) / ll : -1
  const angle = Math.acos(cos > 1 ? 1 : cos < -1 ? -1 : cos)
  const turn = ll > 0 ? assistCorrection(angle, src.weapons.gimbal, src.weapons.aimAssist, Weapons.assist[p]) : 0
  t.leadInGimbal = ll > 0 && (angle <= 0 || turn >= angle)
  t.assist = t.leadInGimbal ? 2 : turn > 0 ? 1 : 0
}

/** Enemies remaining and the contact markers for every other visible ship. */
function updateContacts(state: HudState, src: HudSource): void {
  const ships = src.ships
  const p = src.playerEid
  const cap = state.contactX.length
  let enemies = 0
  let n = 0
  for (let i = 0; i < ships.length; i++) {
    const e = ships[i]
    if (Life.alive[e] !== 1) continue
    if (isHostile(PLAYER_TEAM_ID, Team.id[e])) enemies++
    if (e === p || e === state.target.eid || n >= cap || p < 0 || !state.alive) continue
    interpolate(e, src.alpha, otherPos)
    if (!projectWorld(src.cam, otherPos.x, otherPos.y, otherPos.z, 0, contactPoint)) continue
    state.contactX[n] = contactPoint.x
    state.contactY[n] = contactPoint.y
    state.contactHostile[n] = isHostile(Team.id[p], Team.id[e]) ? 1 : 0
    n++
  }
  // M4 I4: extra sources (strategic traffic contacts that are not ECS ships), same projection.
  if (p >= 0 && state.alive) {
    for (let k = 0; k < contactSources.length; k++) {
      const cs = contactSources[k]
      const count = cs.count()
      for (let i = 0; i < count && n < cap; i++) {
        if (!cs.read(i, extraContact)) continue
        if (!projectWorld(src.cam, extraContact.x, extraContact.y, extraContact.z, 0, contactPoint)) continue
        state.contactX[n] = contactPoint.x
        state.contactY[n] = contactPoint.y
        state.contactHostile[n] = isHostile(Team.id[p], extraContact.team) ? 1 : 0
        n++
      }
    }
  }
  state.enemies = enemies
  state.contactCount = n
}

/** A contact that is not an ECS ship (M4 I4): float64 world position, team and 0..1 hull. */
export interface HudContactOut {
  x: number
  y: number
  z: number
  team: number
  hull: number
}

/** An extra HUD contact source (M4 I4); `read` returns false to skip contact `i`. Both allocation-free. */
export interface HudContactSource {
  count(): number
  read(i: number, out: HudContactOut): boolean
  /** RR-F1 BL-2b: the purpose label of ECS ship `eid` if the source owns it (a cached string), else ''. Allocation-free. */
  labelOf?(eid: number): string
}

const contactSources: HudContactSource[] = []
const extraContact: HudContactOut = { x: 0, y: 0, z: 0, team: 0, hull: 0 }

/** BL-2b: the first non-empty purpose label any contact source gives ship `eid`, else ''. Allocation-free (D-014). */
function purposeOf(eid: number): string {
  for (let k = 0; k < contactSources.length; k++) {
    const label = contactSources[k].labelOf?.(eid) ?? ''
    if (label.length > 0) return label
  }
  return ''
}

/** Adds a contact source drawn with the ECS contacts; returns the function that removes it. */
export function registerContactSource(src: HudContactSource): () => void {
  contactSources.push(src)
  return (): void => {
    const i = contactSources.indexOf(src)
    if (i >= 0) contactSources.splice(i, 1)
  }
}

/** Recomputes `state` for this frame. Allocation-free. */
export function updateHudModel(state: HudState, src: HudSource, tuning: HudTuning): void {
  state.stickX = src.stickX
  state.stickY = src.stickY
  if (!src.paused) advanceTimers(state, src.dt, tuning)
  // H5: a camera switch (never the first frame) restarts the "Chase camera" / "Cockpit" toast.
  if (state.cameraKnown && src.cameraMode !== state.cameraMode) state.cameraToastAge = 0
  state.cameraKnown = true
  state.cameraMode = src.cameraMode
  readEvents(state, src, tuning)

  const p = src.playerEid
  state.alive = p >= 0 && Life.alive[p] === 1
  if (p >= 0 && !state.alive) {
    const r = Life.respawnAt[p] - src.time
    state.respawnIn = r > 0 ? r : 0
  } else {
    state.respawnIn = 0
  }

  if (state.alive) {
    const def = src.defs[Ship.defIndex[p]]
    const vx = Vel.x[p]
    const vy = Vel.y[p]
    const vz = Vel.z[p]
    const speed = Math.sqrt(vx * vx + vy * vy + vz * vz)
    state.speed = speed
    state.faOn = Ship.faOn[p] === 1
    state.boosting = Ship.boosting[p] === 1
    state.hull = clamp01(Hull.value[p] / def.hull)
    state.shield = def.shield > 0 ? clamp01(Shield.value[p] / def.shield) : 0
    state.energy = clamp01(Energy.value[p] / def.energy)
    state.lowHull = state.hull < tuning.lowHullFraction
    const slot = Weapons.primaryIndex[p]
    const wi = selectedPrimary(def, slot)
    const weapon = wi >= 0 ? src.weapons.weapons[wi] : undefined
    state.weaponLabel = weapon ? tuning.weaponLabels[weapon.id] : ''
    state.weaponSlot = slot
    state.hasTorpedo = hasSecondary(def)
    state.torpedoes = Weapons.torpedoes[p]

    interpolate(p, src.alpha, playerPos)
    loadNose(p, src.alpha)
    updateTarget(state, src, tuning, wi)

    // Boresight at the target's distance so it overlays the target when the nose is on it.
    let d = tuning.aimDistance
    if (state.target.eid >= 0) d = state.target.distance > tuning.boresightMinDistance ? state.target.distance : tuning.boresightMinDistance
    projectWorld(src.cam, playerPos.x + noseX * d, playerPos.y + noseY * d, playerPos.z + noseZ * d, 0, state.boresight)
    // Assist circle: the cone's angular radius on screen (vertical focal length in px).
    const cone = src.weapons.aimAssist.cone
    state.assistRadiusPx = Weapons.assist[p] > 0 && src.cam.tanHalfFovY > 0 ? (Math.tan(cone) / src.cam.tanHalfFovY) * src.cam.height * 0.5 : 0

    // Velocity vector: prograde when it is in front of the camera, otherwise the retrograde marker.
    if (speed < tuning.velocityMinSpeed) {
      state.velocityMode = VV.hidden
    } else if (projectDirection(src.cam, vx, vy, vz, 0, state.velocity)) {
      state.velocityMode = VV.prograde
    } else if (projectDirection(src.cam, -vx, -vy, -vz, 0, state.velocity)) {
      state.velocityMode = VV.retrograde
    } else {
      state.velocityMode = VV.hidden
    }

    state.lockProgress = state.hasTorpedo && state.target.eid >= 0 && Lock.target[p] === state.target.eid ? lockProgress(p, src.weapons) : 0
    state.locked = state.lockProgress >= 1 && Lock.locked[p] === 1
  } else {
    state.target.eid = -1
    state.target.leadVisible = false
    state.velocityMode = VV.hidden
    state.lockProgress = 0
    state.locked = false
  }

  // Wedges: angle for the current camera and a smooth fade.
  const fade = tuning.wedge.fadeSeconds
  const wedges = state.wedges
  for (let i = 0; i < wedges.length; i++) {
    const w = wedges[i]
    if (!w.active) continue
    w.angle = directionAngle(src.cam, w.dx, w.dy, w.dz)
    const k = 1 - w.age / fade
    w.intensity = k <= 0 ? 0 : k * (2 - k)
  }

  updateContacts(state, src)
}
